import uniqWith from 'lodash/uniqWith';
import { ActionCreator } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { ID_NEW } from '../../../../../domain/model/category/ICategoryRepository';
import { WebCategory } from '../../../../../domain/model/category/WebCategory';
import { INewsFeed, ViewType } from '../../../../../domain/model/newsfeed/INewsFeedRepository';
import { sessionStorage } from '../../../../../shared/sessionstorage';
import { IConvertedImages } from '../../../common/store/interface/IConvertedImages';
import { ITopStates } from './';

/**
 * SessionStorage用のKEY
 */
const KEY_SESSION = 'YAHOO.JP.News.Top.newsFeed.PC';

/**
 * NewsFeed一回の取得数
 */
const PARAM_RESULTS: number = 50;

/**
 * NewsFeed一度の表示数
 */
const MAX_SHOW_ONCE: number = 50;

/**
 * NewsFeedの最大表示数
 */
const MAX_SHOW_FEED: number = 1000;

const actionTypes = {
  FETCH: 'newsFeed/FETCH',
  FETCH_MORE: 'newsFeed/FETCH_MORE',
  FETCH_FAILURE: 'newsFeed/FETCH_FAILURE',
  SET_OPEN_DISLIKE_POPUP: 'newsFeed/SET_OPEN_DISLIKE_POPUP',
  UPDATE_SESSION_STORAGE: 'newsFeed/UPDATE_SESSION_STORAGE',
  REFLECT_SESSION_STORAGE: 'newsFeed/REFLECT_SESSION_STORAGE',
  LOAD_MORE_MAJOR: 'newsFeed/LOAD_MORE_MAJOR',
  UPDATE_FETCH_STATUS: 'newsFeed/UPDATE_FETCH_STATUS'
} as const;

/**
 * feedを取得する
 *
 * @param categoryId
 * @param subCategoryId
 * @param userToken
 * @param start
 */
const fetchFeed: ActionCreator<ThunkAction<Promise<void>, ITopStates, void, Action>> = (
  categoryId: WebCategory,
  subCategoryId: string,
  userToken: string | null,
  start: number = 0
) => {
  // 0の場合は初回取得とする
  const successAction = start === 0 ? fetchSuccess : fetchMoreSuccess;
  const feedPath = subCategoryId === ID_NEW ? `/api/newsFeed/new/${categoryId}` : `/api/newsFeed/${subCategoryId}`;

  return async (dispatch: ThunkDispatch<ITopStates, void, Action>) => {
    dispatch(updateFetchStatus(true, categoryId, subCategoryId));

    if (userToken === null) {
      dispatch(updateFetchStatus(false, categoryId, subCategoryId));
      dispatch(fetchFailure({ categoryId, subCategoryId, statusCode: 400 }));
      return;
    }

    return fetch(`${feedPath}?start=${start}&results=${PARAM_RESULTS}&device=pc`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { UserToken: userToken }
    })
      .then(res => {
        if (res.ok) {
          return res.json();
        }
        const statusCode = res.status;
        return Promise.reject({ statusCode });
      })
      .then(body => {
        dispatch(updateFetchStatus(false, categoryId, subCategoryId));
        dispatch(successAction({ body, categoryId, subCategoryId }));
      })
      .catch(error => {
        dispatch(updateFetchStatus(false, categoryId, subCategoryId));
        dispatch(fetchFailure({ categoryId, subCategoryId, statusCode: error.statusCode || 400 }));
      });
  };
};

/**
 * 類似記事を表示しないポップアップを表示するか否かを設定
 *
 * @param contentId.categoryId
 * @param payload
 * @param contentId 記事ID
 * @param isOpenDislikePopUp 類似記事を表示しないポップアップを表示するかどうか
 * @param contentId.subCategoryId
 * @param contentId.contentId
 * @param contentId.isOpenDislikePopUp
 * @param payload.categoryId
 * @param payload.subCategoryId
 * @param payload.contentId
 * @param payload.isOpenDislikePopUp
 */
const setIsOpenDislikePopUp = (payload: {
  categoryId: WebCategory;
  subCategoryId: string;
  contentId: string;
  isOpenDislikePopUp: boolean;
}) => ({
  type: actionTypes.SET_OPEN_DISLIKE_POPUP,
  payload
});

/**
 * fetch中であるかを更新する
 *
 * @param isFetching
 * @param categoryId
 * @param subCategoryId
 */
const updateFetchStatus = (isFetching: boolean, categoryId: WebCategory, subCategoryId: string) => {
  return {
    type: actionTypes.UPDATE_FETCH_STATUS,
    payload: {
      isFetching,
      categoryId,
      subCategoryId
    }
  };
};

/**
 * ニュースフィードの初期データ取得が成功した際に呼び出されるアクションを生成します。
 *
 * @param payload - 取得したデータのペイロード。
 * @param payload.body - 取得したデータの本体。
 * @param payload.categoryId - WebカテゴリのID。
 * @param payload.subCategoryId - サブカテゴリのID。
 * @returns アクションオブジェクト。
 */
const fetchSuccess = (payload: {
  // より適切な型にすれば`in`による型ガードが不要になる　バックエンド側と合わせたい
  body: INewsFeed;
  categoryId: WebCategory;
  subCategoryId: string;
}) => ({
  type: actionTypes.FETCH,
  payload
});

/**
 * ニュースフィードの追加データ取得が成功した際に呼び出されるアクションを生成します。
 *
 * @param payload - 取得したデータのペイロード。
 * @param payload.body - 取得したデータの本体。
 * @param payload.categoryId - WebカテゴリのID。
 * @param payload.subCategoryId - サブカテゴリのID。
 * @returns アクションオブジェクト。
 */
const fetchMoreSuccess = (payload: {
  // より適切な型にすれば`in`による型ガードが不要になる　バックエンド側と合わせたい
  body: INewsFeed;
  categoryId: WebCategory;
  subCategoryId: string;
}) => ({
  type: actionTypes.FETCH_MORE,
  payload
});

/**
 * ニュースフィードの取得に失敗した場合のアクションを生成します。
 *
 * @param payload - 失敗の詳細情報を含むオブジェクト。
 * @param payload.categoryId - WebカテゴリのID。
 * @param payload.subCategoryId - サブカテゴリのID。
 * @param payload.statusCode - HTTPステータスコード。
 * @returns FETCH_FAILUREアクションとペイロードを含むオブジェクト。
 */
const fetchFailure = (payload: { categoryId: WebCategory; subCategoryId: string; statusCode: number }) => ({
  type: actionTypes.FETCH_FAILURE,
  payload
});

/**
 * session storageの状態を反映する
 */
const reflectSessionStorage = {
  type: actionTypes.REFLECT_SESSION_STORAGE
} as const;

/**
 * session storageを更新する
 *
 * @param categoryId
 * @param subCategoryId
 */
const updateSessionStorage = (categoryId: WebCategory, subCategoryId: string) => ({
  type: actionTypes.UPDATE_SESSION_STORAGE,
  payload: {
    categoryId,
    subCategoryId
  }
});

const actionsWithoutThunk = {
  updateFetchStatus,
  setIsOpenDislikePopUp,
  reflectSessionStorage,
  updateSessionStorage,
  fetchFailure,
  fetchMoreSuccess,
  fetchSuccess
} as const;

export const actions = {
  fetchFeed,
  setIsOpenDislikePopUp,
  reflectSessionStorage,
  updateSessionStorage
} as const;

type ReturnValueOrItself<T> = T extends (...args: infer _) => infer R ? R : T;

/**
 * Actionの型
 */
export type Action = ReturnValueOrItself<(typeof actionsWithoutThunk)[keyof typeof actionsWithoutThunk]>;

/**
 * NewsFeedに表示する広告要素のinterface
 */
export interface IAdItem {
  viewType: ViewType;
}

/**
 * NewsFeedに表示する記事要素のinterface
 */
export interface INewsFeedItem {
  readonly title: string;
  readonly link: string;
  readonly mediaName: string;
  readonly contentId?: string;
  readonly contentIdType?: string;
  readonly thumbUrl?: string;
  readonly wideThumbUrl?: string;
  readonly resizedWideThumbUrl: IConvertedImages | null;
  readonly resizedThumbUrl: IConvertedImages | null;
  readonly viewType: ViewType;
  readonly video?: {
    readonly duration: string;
  };
  readonly ultSlk?: string;
  readonly isPay: boolean;
  readonly taxIncludedPrice?: number;
  readonly createUnixTimeStamp: number;
  readonly createTime?: {
    readonly date: string;
    readonly time: string;
  };
  readonly isCommentModule: boolean;
  readonly isAuthorCommentModule: boolean;
  readonly authorName?: string;
  readonly authorTitle?: string;
  readonly authorThumbUrl?: string;
  readonly commentText: string;
  readonly dislike?: {
    readonly isSetting: boolean;
  };
  readonly isOpenDislikePopUp: boolean;
}

/**
 * NewsFeedのList情報のinterface
 */
export interface INewsFeedListState {
  readonly items: ReadonlyArray<INewsFeedItem | IAdItem>;
  readonly isError: boolean;
  readonly isErrorOnClient: boolean;
  readonly articleCount: number;
  readonly displayCount: number;
  readonly isLimit: boolean;
  readonly addCount: number;
  readonly isFetching: boolean;
}

/**
 * timelinePFのABテスト用バケット
 */
export interface INewsFeedExperiment {
  experimentId: string;
  bucketId: string;
}

/**
 * FeedListStateの初期値
 */
export const initialListState: INewsFeedListState = {
  items: [],
  isError: false,
  isErrorOnClient: false,
  articleCount: 0,
  displayCount: 0,
  isLimit: false,
  addCount: 0,
  isFetching: true
} as const;

/**
 * INewsFeedListStateをキャッシュするキーを生成する
 *
 * @param categoryId
 * @param subCategoryId
 */
export function convertCacheKey(categoryId: string, subCategoryId: string): string {
  return `${categoryId}-${subCategoryId}`;
}

interface IListStateCache {
  [key: string]: INewsFeedListState;
}

export interface INewsFeedState {
  stateCache: IListStateCache;
}

const initialState: INewsFeedState = {
  stateCache: {}
};

export default (state = initialState, action: Action): INewsFeedState => {
  switch (action.type) {
    case actionTypes.FETCH: {
      const updateState: INewsFeedState = {
        // initial stateを上書きしないようにcacheの状態を移す
        stateCache: { ...state.stateCache }
      };

      const { body, categoryId, subCategoryId } = action.payload;
      const { items, articleCount, isLimit } = body;

      const listState: INewsFeedListState = {
        items: body.items,
        isError: false,
        isErrorOnClient: false,
        articleCount,
        displayCount: items.length,
        isLimit,
        addCount: 0,
        isFetching: false
      };

      const cacheKey = convertCacheKey(categoryId, subCategoryId);
      updateState.stateCache[cacheKey] = listState;

      // session storageに保存
      // 表示中のサブカテゴリのタイムラインしか保存をしない
      sessionStorage.set(KEY_SESSION, {
        [cacheKey]: listState
      });

      return updateState;
    }
    case actionTypes.FETCH_FAILURE: {
      const updateState: INewsFeedState = {
        stateCache: { ...state.stateCache }
      };
      const { categoryId, subCategoryId, statusCode } = action.payload;
      const cacheKey = convertCacheKey(categoryId, subCategoryId);

      const subCategoryState = state.stateCache[cacheKey] || initialListState;

      const isErrorOnClient: boolean = statusCode
        ? statusCode >= 400 && statusCode < 500
        : subCategoryState.isErrorOnClient;

      updateState.stateCache[cacheKey] = {
        ...subCategoryState,
        isError: true,
        isErrorOnClient
      };

      return updateState;
    }
    case actionTypes.FETCH_MORE: {
      const updateState: INewsFeedState = {
        stateCache: { ...state.stateCache }
      };
      const { body, categoryId, subCategoryId } = action.payload;
      const { items, articleCount, isLimit } = body;
      const cacheKey = convertCacheKey(categoryId, subCategoryId);

      const currentListState = updateState.stateCache[cacheKey];

      if (!currentListState) {
        // 追加読み込みの場合にundefinedの場合は問題あり
        return state;
      }

      // 同一データが含まれないように排除する
      const newList = uniqWith([...currentListState.items, ...items], (value, other) => {
        // 広告は重複排除しない
        return value.viewType === ViewType.AD_INFEED || value.viewType === ViewType.AD_SPONSORED
          ? false
          : ('link' in value ? value.link : undefined) === ('link' in other ? other.link : undefined);
      });

      // 一度に表示を追加する数 : MAX_SHOW_ONCEを超えない場合は配列数
      const additionCount: number = newList.length >= MAX_SHOW_ONCE ? MAX_SHOW_ONCE : newList.length;
      const displayCount = currentListState.displayCount + additionCount;

      const listState: INewsFeedListState = {
        items: newList,
        displayCount,
        articleCount: currentListState.articleCount + articleCount,
        isLimit: displayCount >= MAX_SHOW_FEED || isLimit,
        isError: false,
        isErrorOnClient: false,
        addCount: currentListState.addCount + 1,
        isFetching: false
      };

      updateState.stateCache[cacheKey] = listState;

      // session storageに保存
      // 表示中のサブカテゴリのタイムラインしか保存をしない
      sessionStorage.set(KEY_SESSION, {
        [cacheKey]: listState
      });

      return updateState;
    }

    case actionTypes.SET_OPEN_DISLIKE_POPUP: {
      const { categoryId, subCategoryId, contentId, isOpenDislikePopUp } = action.payload;
      if (!categoryId || !subCategoryId) {
        return state;
      }
      const cacheKey = convertCacheKey(categoryId, subCategoryId);
      const updateState: INewsFeedState = {
        // initial stateを上書きしないようにcacheの状態を移す
        stateCache: {
          ...state.stateCache
        }
      };
      if (!updateState.stateCache[cacheKey] || !state.stateCache[cacheKey]) {
        return state;
      }
      updateState.stateCache[cacheKey] = {
        ...state.stateCache[cacheKey],
        items: state.stateCache[cacheKey].items.map(item => {
          if ('contentId' in item && item.contentId === contentId) {
            return {
              ...item,
              isOpenDislikePopUp
            };
          }
          return item;
        })
      };
      return updateState;
    }

    case actionTypes.REFLECT_SESSION_STORAGE: {
      const restoreState: IListStateCache | undefined = sessionStorage.get(KEY_SESSION);

      if (!restoreState) {
        // 保存されていなければ以降の処理は実行しない
        return state;
      }
      if (
        window?.performance?.navigation &&
        window.performance.navigation.type === window.performance.navigation.TYPE_RELOAD
      ) {
        // reloadされた場合はsession情報を破棄して、元のstateを返す
        sessionStorage.remove(KEY_SESSION);
        return state;
      }

      return {
        stateCache: restoreState
      };
    }

    case actionTypes.UPDATE_SESSION_STORAGE: {
      // session storageに指定したsubCategoryIdのデータを反映する
      const { categoryId, subCategoryId } = action.payload;
      const cacheKey = convertCacheKey(categoryId, subCategoryId);

      const currentListState = state.stateCache[cacheKey];

      if (currentListState) {
        // 指定したidのlistStateがあれば保存する
        sessionStorage.set(KEY_SESSION, {
          [cacheKey]: currentListState
        });
      }
      return state;
    }
    case actionTypes.UPDATE_FETCH_STATUS: {
      const { categoryId, subCategoryId } = action.payload;
      const cacheKey = convertCacheKey(categoryId, subCategoryId);
      const updateState: INewsFeedState = {
        stateCache: { ...state.stateCache }
      };

      const currentCategoryState = updateState.stateCache[cacheKey];

      if (currentCategoryState) {
        updateState.stateCache[cacheKey] = {
          ...currentCategoryState,
          isFetching: action.payload.isFetching
        };
      } else {
        updateState.stateCache[cacheKey] = {
          ...initialListState,
          isFetching: action.payload.isFetching
        };
      }
      return updateState;
    }

    default:
      return state;
  }
};
